import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { showToast } from '../components/Toast';
import DmrActionItems, { ItemForm } from '../components/DmrActionItems';
import { dmrApi, type DmrItemInput, type DmrResponse, type DmrTemplateItem } from '../api/dmr';

/**
 * Running one DMR call (#216, phase 2). Marketing works the run sheet top to
 * bottom while screen-sharing the franchisee's dashboard; every line can be
 * marked and turned into an action item. Autosaves. FBCs get the same page
 * read-only. Franchisees never reach it (the API 404s them).
 */

const NAVY = '#1f2a48';
const MUTED = '#64748b';
const BORDER = '#e2e8f0';

const CHECK: Array<[NonNullable<DmrResponse['status']>, string, string]> = [
  ['yes', 'Yes / done', '#15803d'],
  ['needs_action', 'Needs action', '#b45309'],
  ['na', 'N/A', MUTED],
];

function errText(e: unknown) {
  return e instanceof Error ? e.message : 'Something went wrong';
}

export default function DmrCallPage() {
  const id = Number(useParams().id);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['dmr', 'call', id], queryFn: () => dmrApi.call(id), retry: false });

  const [responses, setResponses] = useState<Record<string, DmrResponse>>({});
  const [notes, setNotes] = useState('');
  const [summary, setSummary] = useState('');
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [adding, setAdding] = useState<{ templateItemId?: string; title?: string } | null>(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (!q.data || loaded.current) return;
    loaded.current = true;
    setResponses(q.data.run?.responses ?? {});
    setNotes(q.data.run?.notes ?? '');
    setSummary(q.data.run?.summary ?? '');
    setSavedAt(q.data.run?.updatedAt ?? null);
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => dmrApi.saveRun(id, { responses, notes, summary }),
    onSuccess: (r) => {
      setDirty(false);
      setSavedAt(r.run.updatedAt);
    },
    onError: (e) => showToast(`Not saved: ${errText(e)}`, { variant: 'error', durationMs: 5000 }),
  });

  // Autosave a moment after the last change, so nothing is lost if the
  // call drops or the tab closes.
  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(() => save.mutate(), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, responses, notes, summary]);

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (dirty) e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['dmr'] });
  const addItem = useMutation({
    mutationFn: (body: DmrItemInput) => dmrApi.createItem(id, body),
    onSuccess: () => {
      setAdding(null);
      refresh();
    },
    onError: (e) => showToast(errText(e), { variant: 'error', durationMs: 5000 }),
  });
  const complete = useMutation({
    mutationFn: async () => {
      if (dirty) await dmrApi.saveRun(id, { responses, notes, summary });
      return dmrApi.completeCall(id);
    },
    onSuccess: () => {
      setDirty(false);
      showToast('Call marked done. A summary note was added to their record.');
      refresh();
    },
    onError: (e) => showToast(errText(e), { variant: 'error', durationMs: 5000 }),
  });
  const followup = useMutation({
    mutationFn: () => dmrApi.sendFollowup(id),
    onSuccess: (r) => {
      showToast(r.testMode ? `Test site: recap sent to you only (${r.sent.join(', ')})` : `Recap sent to ${r.sent.length} people`, { durationMs: 4500 });
      refresh();
    },
    onError: (e) => showToast(errText(e), { variant: 'error', durationMs: 5000 }),
  });

  if (q.isLoading) return <Wrap>Loading the call…</Wrap>;
  if (q.error || !q.data) return <Wrap><p style={{ color: MUTED }}>{errText(q.error)}</p></Wrap>;

  const { booking: b, partner, template, actionItems, canEdit } = q.data;
  const setResp = (itemId: string, patch: Partial<DmrResponse>) => {
    setResponses((r) => ({ ...r, [itemId]: { ...r[itemId], ...patch } }));
    setDirty(true);
  };
  const allItems = template?.sections.flatMap((s) => s.items) ?? [];
  const answered = allItems.filter((i) => {
    const r = responses[i.id];
    return r && (r.status || (r.value !== undefined && r.value !== null && r.value !== ''));
  }).length;

  return (
    <Wrap>
      <h1 style={{ margin: '10px 0 2px', fontSize: '1.45rem', color: NAVY }}>
        DMR call: {b.partnerName}
      </h1>
      <p style={{ margin: 0, color: MUTED }}>
        {b.startsAt && new Date(b.startsAt).toLocaleString(undefined, { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}
        {' · '}with {b.teamMember?.name ?? '—'}
        {b.fbcName && ` · FBC ${b.fbcName}`}
        {b.status !== 'scheduled' && ` · ${b.status === 'completed' ? 'Done' : b.status === 'no_show' ? 'No-show' : 'Cancelled'}`}
      </p>

      <section style={{ ...card, marginTop: 14, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 14 }}>
        <div>
          <div style={label}>Open their dashboard</div>
          {b.afs.map((af) => (
            <a key={af} href={`/?af=${encodeURIComponent(af)}`} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600, marginRight: 10 }}>
              {af}
            </a>
          ))}
        </div>
        {partner?.marketingNames.length ? (
          <div>
            <div style={label}>Locations</div>
            {partner.marketingNames.join(' · ')}
          </div>
        ) : null}
        <div>
          <div style={label}>On the invite</div>
          {[b.primaryEmail, ...b.extraAttendees.map((a) => a.name || a.email)].join(', ')}
        </div>
        {b.teamsJoinUrl && (
          <div>
            <div style={label}>Meeting</div>
            <a href={b.teamsJoinUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600 }}>Join on Teams</a>
          </div>
        )}
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(300px, 1fr)', gap: 20, marginTop: 18, alignItems: 'start' }} className="dmr-call-grid">
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h2 style={{ margin: 0, fontSize: 18, color: NAVY }}>Run sheet</h2>
            <span style={{ fontSize: 13, color: MUTED }}>
              {answered} of {allItems.length} covered ·{' '}
              {save.isPending ? 'Saving…' : dirty ? 'Unsaved changes' : savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : 'Not started'}
            </span>
          </div>
          {!canEdit && <p style={{ margin: 0, color: MUTED, fontSize: 14 }}>Read-only: the marketing team fills this in during the call.</p>}
          {template?.sections.map((section) => (
            <section key={section.id} style={card}>
              <h3 style={{ margin: '0 0 4px', fontSize: 16, color: NAVY }}>{section.title}</h3>
              {section.prompt && <p style={{ margin: '0 0 10px', fontSize: 14, color: MUTED, fontStyle: 'italic' }}>{section.prompt}</p>}
              <div style={{ display: 'grid', gap: 10 }}>
                {section.items.map((item) => (
                  <RunItem
                    key={item.id}
                    item={item}
                    value={responses[item.id] ?? {}}
                    disabled={!canEdit}
                    onChange={(patch) => setResp(item.id, patch)}
                    onAddAction={() => setAdding({ templateItemId: item.id, title: item.label })}
                    hasActions={actionItems.some((a) => a.templateItemId === item.id)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div style={{ display: 'grid', gap: 14, position: 'sticky', top: 12 }}>
          <section style={card}>
            <h3 style={{ margin: '0 0 6px', fontSize: 16, color: NAVY }}>Action items</h3>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: MUTED }}>Franchisees see the ones marked visible, with their status.</p>
            <DmrActionItems items={actionItems} mode="edit" canEdit={canEdit} />
            {canEdit &&
              (adding ? (
                <div style={{ marginTop: 10 }}>
                  <ItemForm
                    initial={{ title: adding.title ?? '', templateItemId: adding.templateItemId ?? null }}
                    submitLabel="Add"
                    onSubmit={(body) => addItem.mutate(body)}
                    onCancel={() => setAdding(null)}
                  />
                </div>
              ) : (
                <button style={{ ...linkBtn, marginTop: 8 }} onClick={() => setAdding({})}>+ Add action item</button>
              ))}
          </section>

          <section style={card}>
            <h3 style={{ margin: '0 0 6px', fontSize: 16, color: NAVY }}>Notes</h3>
            <textarea
              value={notes}
              disabled={!canEdit}
              onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
              rows={6}
              placeholder="Working notes during the call (marketing and FBCs only)"
              style={textarea}
            />
            <h3 style={{ margin: '12px 0 6px', fontSize: 16, color: NAVY }}>Summary</h3>
            <textarea
              value={summary}
              disabled={!canEdit}
              onChange={(e) => { setSummary(e.target.value); setDirty(true); }}
              rows={4}
              placeholder="A few sentences for their record and the recap email"
              style={textarea}
            />
            <p style={{ margin: '4px 0 0', fontSize: 12, color: MUTED }}>
              The summary goes into the recap email and their Notes. Working notes stay internal.
            </p>
          </section>

          {canEdit && (
            <section style={card}>
              {b.status === 'scheduled' ? (
                <button
                  style={primaryBtn}
                  disabled={complete.isPending}
                  onClick={() => window.confirm('Mark this call done? A summary note is added to each of their AFs (and Zoho).') && complete.mutate()}
                >
                  {complete.isPending ? 'Saving…' : 'Mark call done'}
                </button>
              ) : (
                <p style={{ margin: '0 0 8px', fontSize: 14 }}>
                  {b.status === 'completed' ? 'Call done.' : b.status === 'no_show' ? 'Marked no-show.' : 'Cancelled.'}
                </p>
              )}
              {b.status === 'completed' && (
                <div style={{ marginTop: 10 }}>
                  <button
                    style={primaryBtn}
                    disabled={followup.isPending}
                    onClick={() => window.confirm(`Email the recap (summary + visible action items) to ${b.partnerName} and their invited staff?`) && followup.mutate()}
                  >
                    {followup.isPending ? 'Sending…' : b.followupSentAt ? 'Send the recap again' : 'Send recap email'}
                  </button>
                  {b.followupSentAt && (
                    <p style={{ margin: '6px 0 0', fontSize: 12, color: MUTED }}>
                      Last sent {new Date(b.followupSentAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              )}
            </section>
          )}
        </div>
      </div>
      <style>{`@media (max-width: 900px) { .dmr-call-grid { grid-template-columns: 1fr !important; } }`}</style>
    </Wrap>
  );
}

function RunItem({
  item,
  value,
  disabled,
  onChange,
  onAddAction,
  hasActions,
}: {
  item: DmrTemplateItem;
  value: DmrResponse;
  disabled: boolean;
  onChange: (patch: Partial<DmrResponse>) => void;
  onAddAction: () => void;
  hasActions: boolean;
}) {
  const [showNote, setShowNote] = useState(Boolean(value.note));
  return (
    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ flex: '1 1 260px', fontSize: 14, color: NAVY }}>{item.label}</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          {item.type === 'check' &&
            CHECK.map(([k, text, color]) => {
              const on = value.status === k;
              return (
                <button
                  key={k}
                  disabled={disabled}
                  onClick={() => onChange({ status: on ? undefined : k })}
                  style={{
                    border: `1px solid ${on ? color : '#cbd5e1'}`, background: on ? color : '#fff', color: on ? '#fff' : color,
                    borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
                  }}
                >
                  {text}
                </button>
              );
            })}
          {item.type === 'choice' &&
            item.options?.map((o) => {
              const on = value.value === o;
              return (
                <button
                  key={o}
                  disabled={disabled}
                  onClick={() => onChange({ value: on ? null : o })}
                  style={{
                    border: `1px solid ${on ? NAVY : '#cbd5e1'}`, background: on ? NAVY : '#fff', color: on ? '#fff' : NAVY,
                    borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
                  }}
                >
                  {o}
                </button>
              );
            })}
          {item.type === 'number' && (
            <input
              type="number"
              disabled={disabled}
              value={value.value ?? ''}
              onChange={(e) => onChange({ value: e.target.value === '' ? null : Number(e.target.value) })}
              style={{ width: 110, padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6 }}
            />
          )}
        </div>
      </div>
      {item.type === 'text' && (
        <textarea disabled={disabled} value={String(value.value ?? '')} onChange={(e) => onChange({ value: e.target.value })} rows={2} style={{ ...textarea, marginTop: 6 }} />
      )}
      {(showNote || value.note) && (
        <input
          disabled={disabled}
          value={value.note ?? ''}
          onChange={(e) => onChange({ note: e.target.value })}
          placeholder="Note"
          style={{ marginTop: 6, width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}
        />
      )}
      {!disabled && (
        <div style={{ marginTop: 4 }}>
          {!showNote && !value.note && <button style={linkBtn} onClick={() => setShowNote(true)}>+ note</button>}
          <button style={linkBtn} onClick={onAddAction}>+ action item</button>
          {hasActions && <span style={{ fontSize: 12, color: '#15803d', fontWeight: 600 }}>✓ has action item</span>}
        </div>
      )}
    </div>
  );
}

function Wrap({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '22px 24px 70px', maxWidth: 1280, margin: '0 auto' }}>
      <p style={{ margin: 0 }}>
        <Link to="/dmr" style={{ color: '#2563eb' }}>← DMR calls</Link>
      </p>
      {children}
    </div>
  );
}

const card: React.CSSProperties = { border: `1px solid ${BORDER}`, borderRadius: 10, padding: '14px 16px', background: '#fff' };
const label: React.CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: MUTED, fontWeight: 700, marginBottom: 2 };
const textarea: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, fontFamily: 'inherit',
};
const primaryBtn: React.CSSProperties = {
  background: NAVY, color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
};
const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12, padding: '0 8px 0 0', fontWeight: 600,
};
